import type { FilterConfigItem } from "@shared/components/DynamicFilter/index.vue";
import type { PaginatedQuery } from "@shared/types/list";
import { DEFAULT_PAGE_SIZE } from "@shared/types/list";
import { resolveMultiSelectForRequest } from "@shared/utils/filterQuery";
import { t } from "@shared/utils/i18n";
import { CORP_COOPERATION_STATUS_OPTIONS } from "./data";

export interface CorpQueryParam extends PaginatedQuery {
    /** 企业多选 → corpIds */
    corpIds: string[];
    /** 合作状态单选（UiSelect 存数组） */
    cooperationStatus: (number | string)[];
    /** 渠道多选 → channelIds */
    channelIds: string[];
    contactNameLike: string;
    contactTelephoneLike: string;
}

export interface CorpListRequestParams {
    page: number;
    perPage: number;
    corpIds?: string[];
    cooperationStatus?: number;
    channelIds?: string[];
    contactNameLike?: string;
    contactTelephoneLike?: string;
}

export function createDefaultCorpQuery(): CorpQueryParam {
    return {
        corpIds: [],
        cooperationStatus: [],
        channelIds: [],
        contactNameLike: "",
        contactTelephoneLike: "",
        page: 1,
        limit: DEFAULT_PAGE_SIZE,
    };
}

export const DEFAULT_CORP_QUERY: CorpQueryParam = createDefaultCorpQuery();

function toNumberArray(values?: (number | string)[]): number[] {
    const resolved = resolveMultiSelectForRequest(values);
    if (!resolved?.length) return [];
    return resolved.map(v => Number(v)).filter(n => !Number.isNaN(n));
}

function toSingleNumber(values?: (number | string)[]): number | undefined {
    const nums = toNumberArray(values);
    return nums.length ? nums[0] : undefined;
}

export function toCorpListRequestParams(query: CorpQueryParam): CorpListRequestParams {
    const params: CorpListRequestParams = {
        page: query.page,
        perPage: query.limit,
    };

    if (query.corpIds.length) params.corpIds = query.corpIds;

    const cooperationStatus = toSingleNumber(query.cooperationStatus);
    if (cooperationStatus != null) params.cooperationStatus = cooperationStatus;

    const channelIds = resolveMultiSelectForRequest(query.channelIds)?.map(v => String(v)) ?? [];
    if (channelIds.length) params.channelIds = channelIds;

    const contactNameLike = query.contactNameLike?.trim();
    if (contactNameLike) params.contactNameLike = contactNameLike;

    const contactTelephoneLike = query.contactTelephoneLike?.trim();
    if (contactTelephoneLike) params.contactTelephoneLike = contactTelephoneLike;

    return params;
}

export function buildCorpListFilterConfig(corpOptions: { label: string; value: string }[], channelOptions: { label: string; value: string }[]): FilterConfigItem[] {
    return [
        {
            type: "UiMultipleSelect",
            key: "corpIds",
            label: t("企业名称"),
            placeholder: t("企业名称"),
            options: corpOptions,
            width: 160,
            props: { filterable: true },
        },
        {
            type: "UiSelect",
            key: "cooperationStatus",
            label: t("合作状态"),
            placeholder: t("合作状态"),
            options: CORP_COOPERATION_STATUS_OPTIONS,
            width: 120,
        },
        {
            type: "UiMultipleSelect",
            key: "channelIds",
            label: t("关联渠道"),
            placeholder: t("关联渠道"),
            options: channelOptions,
            width: 160,
            props: { filterable: true },
        },
        {
            type: "Input",
            key: "contactNameLike",
            label: t("联系人"),
            placeholder: t("联系人"),
            width: 140,
        },
        {
            type: "Input",
            key: "contactTelephoneLike",
            label: t("手机号"),
            placeholder: t("手机号"),
            width: 140,
        },
    ];
}
